import { useState, useEffect, useCallback, useRef } from "react";
import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import {
  getAssociatedTokenAddressSync,
  createBurnCheckedInstruction,
  createTransferCheckedInstruction,
} from "@solana/spl-token";

// Custom RPC key first, then 4 confirmed-working free endpoints (CORS-safe in browser).
const SOL_RPC_ENTRIES = [
  ...(process.env.REACT_APP_SOL_RPC ? [{ url: process.env.REACT_APP_SOL_RPC }] : []),
  { url: "https://api.mainnet-beta.solana.com" },
  { url: "https://solana.api.pocket.network/" },
  { url: "https://public.rpc.solanavibestation.com/" },
  { url: "https://api.uniblock.dev/uni/v1/json-rpc?chainId=solana" },
  { url: "https://solana-mainnet.gateway.tatum.io/" },
];
const LAMPORTS_PER_SOL = 1_000_000_000;
const MEMO_PROGRAM_ID = new PublicKey(
  "MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr"
);

function getPhantom() {
  return window.phantom?.solana || (window.solana?.isPhantom ? window.solana : null);
}

function getSolflare() {
  return window.solflare?.isSolflare ? window.solflare : null;
}

function makeConnection({ url, headers }) {
  return new Connection(url, headers ? { commitment: "confirmed", httpHeaders: headers } : "confirmed");
}

async function getWorkingConnection() {
  for (const entry of SOL_RPC_ENTRIES) {
    try {
      const conn = makeConnection(entry);
      await conn.getSlot("confirmed");
      return conn;
    } catch { /* try next */ }
  }
  return makeConnection(SOL_RPC_ENTRIES[0]);
}

const fetchSolBalance = async (address) => {
  for (const entry of SOL_RPC_ENTRIES) {
    try {
      const connection = makeConnection(entry);
      const lamports = await connection.getBalance(new PublicKey(address));
      return BigInt(lamports);
    } catch { /* try next */ }
  }
  return null;
};

// Fetch SPL token balance for a given mint, returns amount in smallest units (BigInt) or null
const fetchSplTokenBalance = async (ownerAddress, mintAddress) => {
  for (const entry of SOL_RPC_ENTRIES) {
    try {
      const connection = makeConnection(entry);
      const ownerPubkey = new PublicKey(ownerAddress);
      const mintPubkey = new PublicKey(mintAddress);
      const accounts = await connection.getParsedTokenAccountsByOwner(ownerPubkey, { mint: mintPubkey });
      if (accounts.value.length === 0) return 0n;
      const amount = accounts.value[0].account.data.parsed.info.tokenAmount.amount;
      return BigInt(amount);
    } catch { /* try next */ }
  }
  return null;
};

export const useSolWallet = ({ enabled = false, splMintAddress = null, splDecimals = 9 } = {}) => {
  const [phantomAvailable, setPhantomAvailable] = useState(false);
  const [solflareAvailable, setSolflareAvailable] = useState(false);
  const [connected, setConnected] = useState(false);
  const [address, setAddress] = useState("");
  const [walletType, setWalletType] = useState(null); // "phantom" | "solflare"
  const [balanceLamports, setBalanceLamports] = useState(null);
  const [splBalanceSmallest, setSplBalanceSmallest] = useState(null); // SPL token balance in smallest units
  const balancePollRef = useRef(null);
  const splBalancePollRef = useRef(null);

  // Detect wallets
  useEffect(() => {
    if (!enabled) {
      setPhantomAvailable(false);
      setSolflareAvailable(false);
      return;
    }
    const check = () => {
      setPhantomAvailable(!!getPhantom());
      setSolflareAvailable(!!getSolflare());
    };
    check();
    // wallets may inject after page load
    const t = setTimeout(check, 800);
    return () => clearTimeout(t);
  }, [enabled]);

  // Poll balance
  useEffect(() => {
    if (!enabled || !connected || !address) {
      setBalanceLamports(null);
      if (balancePollRef.current) clearInterval(balancePollRef.current);
      return;
    }

    fetchSolBalance(address).then(setBalanceLamports);
    balancePollRef.current = setInterval(() => {
      fetchSolBalance(address).then(setBalanceLamports);
    }, 15_000);

    return () => {
      if (balancePollRef.current) clearInterval(balancePollRef.current);
    };
  }, [enabled, connected, address]);

  // Poll SPL token balance when a mint address is provided
  useEffect(() => {
    if (!enabled || !connected || !address || !splMintAddress) {
      setSplBalanceSmallest(null);
      if (splBalancePollRef.current) clearInterval(splBalancePollRef.current);
      return;
    }

    fetchSplTokenBalance(address, splMintAddress).then(setSplBalanceSmallest);
    splBalancePollRef.current = setInterval(() => {
      fetchSplTokenBalance(address, splMintAddress).then(setSplBalanceSmallest);
    }, 15_000);

    return () => {
      if (splBalancePollRef.current) clearInterval(splBalancePollRef.current);
    };
  }, [enabled, connected, address, splMintAddress]);

  // Reset on disable
  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      setAddress("");
      setWalletType(null);
      setSplBalanceSmallest(null);
    }
  }, [enabled]);

  const connectPhantom = useCallback(async () => {
    const phantom = getPhantom();
    if (!phantom) throw new Error("Phantom wallet is not installed.");
    const res = await phantom.connect({
      appIdentity: {
        name: "BESC Bridge",
        url: "https://wbescbridge.com",
        icon: "https://wbescbridge.com/logo192.png",
        app_id: "e39dc1d3-3607-4af0-a79c-8b84f3ce5166",
      },
    });
    const addr = res.publicKey.toString();
    setConnected(true);
    setAddress(addr);
    setWalletType("phantom");
    return addr;
  }, []);

  const connectSolflare = useCallback(async () => {
    const solflare = getSolflare();
    if (!solflare) throw new Error("Solflare wallet is not installed.");
    await solflare.connect();
    const addr = solflare.publicKey.toString();
    setConnected(true);
    setAddress(addr);
    setWalletType("solflare");
    return addr;
  }, []);

  const disconnect = useCallback(async () => {
    try {
      if (walletType === "phantom") await getPhantom()?.disconnect();
      if (walletType === "solflare") await getSolflare()?.disconnect();
    } catch { /* no-op */ }
    setConnected(false);
    setAddress("");
    setWalletType(null);
  }, [walletType]);

  const send = useCallback(
    async ({ lamports, destination, bescAddress }) => {
      if (!connected || !address)
        throw new Error("No Solana wallet connected.");

      const wallet = walletType === "phantom" ? getPhantom() : getSolflare();
      if (!wallet) throw new Error("Wallet not found.");

      const connection = await getWorkingConnection();
      const fromPubkey = new PublicKey(address);
      const toPubkey = new PublicKey(destination);

      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");

      const tx = new Transaction({
        recentBlockhash: blockhash,
        feePayer: fromPubkey,
      });

      tx.add(
        SystemProgram.transfer({
          fromPubkey,
          toPubkey,
          lamports: BigInt(lamports),
        })
      );

      // Attach BESC destination address as SPL Memo
      tx.add(
        new TransactionInstruction({
          keys: [{ pubkey: fromPubkey, isSigner: true, isWritable: false }],
          programId: MEMO_PROGRAM_ID,
          data: new TextEncoder().encode(bescAddress),
        })
      );

      let signature;
      if (walletType === "phantom") {
        const { signature: sig } = await wallet.signAndSendTransaction(tx);
        signature = sig;
      } else {
        const signed = await wallet.signTransaction(tx);
        signature = await connection.sendRawTransaction(signed.serialize());
      }

      await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, "confirmed");
      return signature;
    },
    [connected, address, walletType]
  );

  // Burn WBESC SPL tokens and attach destination memo (SOL → EVM direction)
  // memo format: "CHAIN:0xEVMAddress" e.g. "ETH:0xAbcd..."
  const burnSplToken = useCallback(
    async ({ mintAddress, decimals, amount, destinationChain, evmAddress }) => {
      if (!connected || !address) throw new Error("No Solana wallet connected.");
      if (!mintAddress) throw new Error("SPL mint address not configured.");
      if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(mintAddress)) throw new Error("Invalid SPL mint address.");
      if (!/^(ETH|BSC|BESC|RH)$/.test(destinationChain)) throw new Error("Invalid destination chain.");
      if (!/^0x[0-9a-fA-F]{40}$/.test(evmAddress)) throw new Error("Invalid EVM destination address.");

      const wallet = walletType === "phantom" ? getPhantom() : getSolflare();
      if (!wallet) throw new Error("Wallet not found.");

      const connection = await getWorkingConnection();
      const ownerPubkey = new PublicKey(address);
      const mintPubkey = new PublicKey(mintAddress);
      const ata = getAssociatedTokenAddressSync(mintPubkey, ownerPubkey);

      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");
      const tx = new Transaction({ recentBlockhash: blockhash, feePayer: ownerPubkey });

      // Burn the SPL tokens — user owns the ATA, so they are authority
      tx.add(createBurnCheckedInstruction(
        ata,
        mintPubkey,
        ownerPubkey,
        BigInt(amount),
        decimals,
      ));

      // Attach destination as memo: "CHAIN:0xEVMAddress"
      const memoText = `${destinationChain}:${evmAddress}`;
      tx.add(
        new TransactionInstruction({
          keys: [{ pubkey: ownerPubkey, isSigner: true, isWritable: false }],
          programId: MEMO_PROGRAM_ID,
          data: new TextEncoder().encode(memoText),
        })
      );

      let signature;
      if (walletType === "phantom") {
        const { signature: sig } = await wallet.signAndSendTransaction(tx);
        signature = sig;
      } else {
        const signed = await wallet.signTransaction(tx);
        signature = await connection.sendRawTransaction(signed.serialize());
      }

      await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, "confirmed");
      return signature;
    },
    [connected, address, walletType]
  );

  // Send SPL tokens to bridge wallet — for custom SOL tokens bridging to BESC.
  // Transfers the token from the user's ATA to the bridge's ATA, with a memo
  // containing the user's BESC destination address.
  const sendSplToken = useCallback(
    async ({ mintAddress, decimals, amount, destination, bescAddress }) => {
      if (!connected || !address) throw new Error("No Solana wallet connected.");
      if (!mintAddress) throw new Error("SPL mint address not configured.");
      if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(mintAddress)) throw new Error("Invalid SPL mint address.");
      if (!/^0x[0-9a-fA-F]{40}$/.test(bescAddress)) throw new Error("Invalid BESC destination address.");

      const wallet = walletType === "phantom" ? getPhantom() : getSolflare();
      if (!wallet) throw new Error("Wallet not found.");

      const connection = await getWorkingConnection();
      const ownerPubkey = new PublicKey(address);
      const mintPubkey = new PublicKey(mintAddress);
      const destPubkey = new PublicKey(destination);

      // Find the user's associated token account for this mint
      const fromAta = getAssociatedTokenAddressSync(mintPubkey, ownerPubkey);
      // Find the bridge wallet's ATA for this mint
      const toAta = getAssociatedTokenAddressSync(mintPubkey, destPubkey);

      // Verify the bridge wallet's ATA exists — if not, the transfer will fail on-chain.
      // The bridge operator must create the ATA before users can deposit this token.
      const toAtaInfo = await connection.getAccountInfo(toAta);
      if (!toAtaInfo) {
        throw new Error("Bridge wallet has not yet initialized an account for this token. Please contact support.");
      }

      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");
      const tx = new Transaction({ recentBlockhash: blockhash, feePayer: ownerPubkey });

      tx.add(
        createTransferCheckedInstruction(
          fromAta,
          mintPubkey,
          toAta,
          ownerPubkey,
          BigInt(amount),
          decimals,
        )
      );

      // Attach BESC destination address as memo
      tx.add(
        new TransactionInstruction({
          keys: [{ pubkey: ownerPubkey, isSigner: true, isWritable: false }],
          programId: MEMO_PROGRAM_ID,
          data: new TextEncoder().encode(bescAddress),
        })
      );

      let signature;
      if (walletType === "phantom") {
        const { signature: sig } = await wallet.signAndSendTransaction(tx);
        signature = sig;
      } else {
        const signed = await wallet.signTransaction(tx);
        signature = await connection.sendRawTransaction(signed.serialize());
      }

      await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, "confirmed");
      return signature;
    },
    [connected, address, walletType]
  );

  const formattedBalance =
    balanceLamports !== null
      ? (Number(balanceLamports) / LAMPORTS_PER_SOL).toLocaleString("en-US", {
          maximumFractionDigits: 6,
        })
      : null;

  const splFormattedBalance =
    splBalanceSmallest !== null
      ? (Number(splBalanceSmallest) / 10 ** splDecimals).toLocaleString("en-US", {
          maximumFractionDigits: splDecimals,
        })
      : null;

  return {
    phantomAvailable,
    solflareAvailable,
    connected,
    address,
    walletType,
    balanceLamports,
    formattedBalance,
    splBalanceSmallest,
    splFormattedBalance,
    connectPhantom,
    connectSolflare,
    disconnect,
    send,
    burnSplToken,
    sendSplToken,
  };
};
